/**
 * CBE SuperApp Mini App payment helpers.
 * Backend generates sign / confirm_payload / xAccessToken.
 * Frontend calls createChechePayment then window.cbesuperapp.initiatePayment.
 */

import {
  getCbeWindow,
  type CbeAuthPayload,
  type CbeCreateOrderRequest,
  type CbeCreateOrderResponse,
  type CbeInitiatePaymentParams,
  type CbePaymentResult,
} from "@/types/cbe-superapp"
import { CBE_APP_CODE, CBE_CREATE_ORDER_ID, CBE_CREATE_PAYMENT_URL, PAY_API_URL } from "@/lib/cbe-config"
import { isCbeSuperAppAvailable } from "@/lib/payment-provider"
import { cbeDebugError, cbeDebugLog, maskSecret } from "@/lib/cbe-debug"

export { CBE_APP_CODE, CBE_CREATE_ORDER_ID, CBE_CREATE_PAYMENT_URL, PAY_API_URL }

const ACCESS_TOKEN_CALLBACK = "handleAccessToken"
const PAYMENT_RESULT_CALLBACK = "handlePaymentResult"

function sendToCbe(payload: Record<string, unknown>): void {
  if (!isCbeSuperAppAvailable()) {
    cbeDebugError("sendToCbe failed — window.cbesuperapp missing")
    throw new Error("CBE_SUPERAPP_UNAVAILABLE")
  }
  const raw = JSON.stringify(payload)
  cbeDebugLog("cbesuperapp.send →", payload.functionName, {
    payloadKeys: Object.keys(payload),
    paramsKeys:
      payload.params && typeof payload.params === "object"
        ? Object.keys(payload.params as object)
        : [],
    rawLength: raw.length,
  })
  getCbeWindow().cbesuperapp!.send(raw)
}

/**
 * Optional: request a user access token from the CBE SuperApp SDK.
 * Prefer backend-generated xAccessToken when available.
 */
export function fetchCbeAccessToken(options?: {
  appCode?: string
  timeoutMs?: number
}): Promise<string> {
  const appCode = options?.appCode || CBE_APP_CODE
  const timeoutMs = options?.timeoutMs ?? 15_000
  const w = getCbeWindow()

  return new Promise((resolve, reject) => {
    if (!isCbeSuperAppAvailable()) {
      reject(new Error("CBE_SUPERAPP_UNAVAILABLE"))
      return
    }

    cbeDebugLog("fetchAccessToken start", { appCode, timeoutMs })

    const previous = w.handleAccessToken
    let settled = false

    const cleanup = () => {
      w.handleAccessToken = previous
      clearTimeout(timer)
    }

    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      cleanup()
      cbeDebugError("fetchAccessToken timeout")
      reject(new Error("CBE_ACCESS_TOKEN_TIMEOUT"))
    }, timeoutMs)

    w.handleAccessToken = (accessToken: string) => {
      if (settled) return
      settled = true
      cleanup()
      if (!accessToken) {
        cbeDebugError("fetchAccessToken empty token")
        reject(new Error("CBE_ACCESS_TOKEN_EMPTY"))
        return
      }
      cbeDebugLog("fetchAccessToken success", { token: maskSecret(accessToken) })
      resolve(accessToken)
    }

    try {
      sendToCbe({
        functionName: "fetchAccessToken",
        params: {
          appcode: appCode,
          callbackName: ACCESS_TOKEN_CALLBACK,
        },
      })
    } catch (err) {
      if (!settled) {
        settled = true
        cleanup()
        cbeDebugError("fetchAccessToken send error", err)
        reject(err)
      }
    }
  })
}

/**
 * Create a signed CBE payment via Qetero createChechePayment.
 * Backend returns orderPayload + authPayload (with xAccessToken) + initiatePaymentParams.
 */
export async function createCbeOrder(
  body: CbeCreateOrderRequest
): Promise<CbeCreateOrderResponse> {
  const url = CBE_CREATE_PAYMENT_URL
  const payload = {
    appointment_id: body.transaction_id,
    type: body.type || "service Fee",
    amount: typeof body.amount === "number" ? String(body.amount) : body.amount,
    title: body.title,
    paymentType: body.paymentType || "InApp",
    ...(body.paymentMethod ? { paymentMethod: body.paymentMethod } : { paymentMethod: CBE_CREATE_ORDER_ID }),
    ...(body.access_token ? { access_token: body.access_token } : {}),
  }

  cbeDebugLog("createChechePayment request", { url, payload })

  let res: Response
  try {
    res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
  } catch (networkErr) {
    cbeDebugError("createChechePayment network error", networkErr)
    throw networkErr
  }

  const rawText = await res.text().catch(() => "")
  cbeDebugLog("createChechePayment HTTP", {
    status: res.status,
    statusText: res.statusText,
    ok: res.ok,
    bodyPreview: rawText.slice(0, 2000),
  })

  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      throw new Error(`CBE_AUTH_EXPIRED: ${res.status} ${res.statusText}${rawText ? ` - ${rawText}` : ""}`)
    }
    throw new Error(`CBE createChechePayment failed: ${res.status} ${res.statusText}${rawText ? ` - ${rawText}` : ""}`)
  }

  let data: CbeCreateOrderResponse
  try {
    data = JSON.parse(rawText) as CbeCreateOrderResponse
  } catch (parseErr) {
    cbeDebugError("createChechePayment JSON parse failed", parseErr, rawText.slice(0, 500))
    throw new Error("CBE createChechePayment response is not valid JSON")
  }

  // Support both { result: {...} } and nested wrappers from Qetero API
  const result =
    data?.result ||
    (data as any)?.data?.result ||
    (data as any)?.data ||
    (data as any)

  cbeDebugLog("createChechePayment parsed result", {
    success: data?.success ?? (data as any)?.status,
    message: data?.message,
    hasOrderPayload: Boolean(result?.orderPayload),
    hasAuthPayload: Boolean(result?.authPayload),
    hasInitiatePaymentParams: Boolean(result?.initiatePaymentParams),
    appName: result?.appName,
    merchantReference: result?.merchantReference,
    amount: result?.amount,
    xAccessToken: maskSecret(result?.authPayload?.xAccessToken),
    xApiKey: maskSecret(result?.authPayload?.xApiKey),
    orderPayload: result?.orderPayload,
    initiatePaymentParamsKeys: result?.initiatePaymentParams
      ? Object.keys(result.initiatePaymentParams)
      : [],
  })

  if (!result?.orderPayload || !result?.authPayload) {
    cbeDebugError("createChechePayment missing orderPayload/authPayload", data)
    throw new Error("CBE createChechePayment response missing orderPayload/authPayload")
  }
  if (!result.authPayload.xAccessToken) {
    cbeDebugError("createChechePayment missing xAccessToken", result.authPayload)
    throw new Error("CBE createChechePayment response missing authPayload.xAccessToken")
  }

  return { ...data, result }
}

/**
 * Launch CBE native payment UI with backend payloads.
 * Uses authPayload as returned (backend already set xAccessToken).
 * Optionally override xAccessToken if the caller fetched a SuperApp user token.
 */
export function initiateCbePayment(options: {
  orderPayload: unknown
  authPayload: CbeAuthPayload
  accessToken?: string
  appName?: string
  timeoutMs?: number
}): Promise<CbePaymentResult> {
  const { orderPayload, appName = "Qetero", timeoutMs = 120_000 } = options
  const authPayload: CbeAuthPayload = {
    ...options.authPayload,
    ...(options.accessToken ? { xAccessToken: options.accessToken } : {}),
  }

  cbeDebugLog("initiatePayment prepare", {
    appName,
    hasOrderPayload: Boolean(orderPayload),
    authKeys: Object.keys(authPayload),
    xAccessToken: maskSecret(authPayload.xAccessToken),
    xApiKey: maskSecret(authPayload.xApiKey),
    overrideToken: Boolean(options.accessToken),
    cbesuperapp: isCbeSuperAppAvailable(),
  })

  if (!authPayload.xAccessToken) {
    cbeDebugError("initiatePayment aborted — missing xAccessToken")
    return Promise.reject(new Error("CBE_MISSING_ACCESS_TOKEN"))
  }

  const w = getCbeWindow()

  return new Promise((resolve, reject) => {
    if (!isCbeSuperAppAvailable()) {
      cbeDebugError("initiatePayment aborted — CBE_SUPERAPP_UNAVAILABLE")
      reject(new Error("CBE_SUPERAPP_UNAVAILABLE"))
      return
    }

    const previous = w.handlePaymentResult
    let settled = false

    const cleanup = () => {
      w.handlePaymentResult = previous
      clearTimeout(timer)
    }

    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      cleanup()
      cbeDebugError("initiatePayment timeout — no handlePaymentResult callback", { timeoutMs })
      reject(new Error("CBE_PAYMENT_TIMEOUT"))
    }, timeoutMs)

    w.handlePaymentResult = (result: CbePaymentResult) => {
      if (settled) return
      settled = true
      cleanup()
      cbeDebugLog("handlePaymentResult callback", result)
      resolve(result ?? {})
    }

    try {
      const message = {
        functionName: "initiatePayment",
        params: {
          orderPayload,
          authPayload,
          callbackName: PAYMENT_RESULT_CALLBACK,
          appName,
        },
        callbackName: PAYMENT_RESULT_CALLBACK,
      }
      cbeDebugLog("initiatePayment sending to SDK", {
        functionName: message.functionName,
        appName,
        callbackName: PAYMENT_RESULT_CALLBACK,
        orderPayload,
        authPayload: {
          ...authPayload,
          xAccessToken: maskSecret(authPayload.xAccessToken),
          xApiKey: maskSecret(authPayload.xApiKey),
        },
      })
      sendToCbe(message)
      cbeDebugLog("initiatePayment send() completed — waiting for handlePaymentResult")
    } catch (err) {
      if (!settled) {
        settled = true
        cleanup()
        cbeDebugError("initiatePayment send() threw", err)
        reject(err)
      }
    }
  })
}

/** Prefer backend initiatePaymentParams when present */
export function initiateCbePaymentFromParams(
  params: CbeInitiatePaymentParams,
  options?: { accessToken?: string; timeoutMs?: number }
): Promise<CbePaymentResult> {
  cbeDebugLog("using backend initiatePaymentParams")
  return initiateCbePayment({
    orderPayload: params.orderPayload,
    authPayload: params.authPayload,
    accessToken: options?.accessToken,
    appName: params.appName || "Qetero",
    timeoutMs: options?.timeoutMs,
  })
}

/**
 * Full pay flow (backend-owned token):
 * createOrder → initiatePayment with returned payloads (no SDK fetchAccessToken required)
 */
export async function payWithCbeSuperApp(options: {
  transactionId: string
  amount: number
  title: string
  /** Optional SuperApp user token override; otherwise use backend xAccessToken */
  accessToken?: string
  appName?: string
}): Promise<CbePaymentResult> {
  cbeDebugLog("payWithCbeSuperApp start", {
    transactionId: options.transactionId,
    amount: options.amount,
    title: options.title,
    hasSdkOverrideToken: Boolean(options.accessToken),
    payApiUrl: PAY_API_URL,
    createPaymentUrl: CBE_CREATE_PAYMENT_URL,
    cbesuperappAvailable: isCbeSuperAppAvailable(),
  })

  try {
    const createOrderResponse = await createCbeOrder({
      transaction_id: options.transactionId,
      title: options.title,
      paymentType: "InApp",
      amount: options.amount,
      ...(options.accessToken ? { access_token: options.accessToken } : {}),
    })

    const { result } = createOrderResponse
    const ready = result.initiatePaymentParams

    cbeDebugLog("createChechePayment OK — launching initiatePayment", {
      path: ready?.orderPayload && ready?.authPayload ? "initiatePaymentParams" : "orderPayload+authPayload",
    })

    if (ready?.orderPayload && ready?.authPayload) {
      return await initiateCbePaymentFromParams(ready, {
        accessToken: options.accessToken,
      })
    }

    return await initiateCbePayment({
      orderPayload: result.orderPayload,
      authPayload: result.authPayload,
      accessToken: options.accessToken,
      appName: result.appName || options.appName || "Qetero",
    })
  } catch (err) {
    cbeDebugError("payWithCbeSuperApp failed", err)
    throw err
  }
}

export function isCbePaymentSuccess(result: CbePaymentResult | null | undefined): boolean {
  return Boolean(result?.ft_number)
}

/** Detect expired / invalid CBE access token from API or SDK errors. */
export function isCbeAuthError(err: unknown): boolean {
  const message = String((err as any)?.message || err || "").toLowerCase()
  return (
    message.includes("cbe_auth_expired") ||
    message.includes("cbe_missing_access_token") ||
    message.includes("unauthorized") ||
    message.includes("forbidden") ||
    message.includes("invalid token") ||
    message.includes("expired") ||
    /\b401\b/.test(message) ||
    /\b403\b/.test(message)
  )
}
